import { InventoryLedgerFilters, LedgerCategory } from './types'

export const LEDGER_ENDPOINT = '/api/inventory-transaction-ledger/'
export const LEDGER_EXPORT_ENDPOINT = '/api/inventory-transaction-ledger/export/'
export const DEFAULT_PAGE = 1
export const DEFAULT_PAGE_SIZE = 25

export const CATEGORY_OPTIONS: { value: LedgerCategory; label: string }[] = [
  { value: 'adjustment', label: 'Adjustment' },
  { value: 'movement', label: 'Movement' },
  { value: 'order', label: 'Order' },
  { value: 'asn', label: 'ASN' },
  { value: 'project', label: 'Project' },
]

const DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/

const isCategory = (value: string): value is LedgerCategory => CATEGORY_OPTIONS.some((option) => option.value === value)

const formatLocalCalendarDate = (date: Date): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export const getDefaultLedgerFilters = (date = new Date()): InventoryLedgerFilters => ({
  dateFrom: formatLocalCalendarDate(new Date(date.getFullYear(), date.getMonth(), 1)),
  dateTo: formatLocalCalendarDate(date),
  categories: [],
  sku: '',
  reference: '',
})

const normalizeDateForInput = (value: string | null): string => {
  if (!value) return ''
  if (DATE_ONLY_REGEX.test(value)) {
    const [year, month, day] = value.split('-').map(Number)
    const parsed = new Date(Date.UTC(year, month - 1, day))
    if (parsed.getUTCFullYear() !== year || parsed.getUTCMonth() !== month - 1 || parsed.getUTCDate() !== day) {
      return ''
    }
    return value
  }
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return ''
  return parsed.toISOString().slice(0, 10)
}

const normalizeDateFrom = (value: string): string => {
  const trimmed = value.trim()
  if (!trimmed) return ''
  if (DATE_ONLY_REGEX.test(trimmed)) return `${trimmed}T00:00:00Z`
  return trimmed
}

export const parseFiltersFromSearch = (search: string, date = new Date()): InventoryLedgerFilters => {
  const params = new URLSearchParams(search)

  const rawCategories = params.getAll('category').flatMap((value) =>
    value
      .split(',')
      .map((part) => part.trim().toLowerCase())
      .filter(Boolean)
  )

  const categories = Array.from(new Set(rawCategories.filter(isCategory))) as LedgerCategory[]

  const filters = {
    dateFrom: normalizeDateForInput(params.get('date_from')),
    dateTo: normalizeDateForInput(params.get('date_to')),
    categories,
    sku: params.get('sku') || '',
    reference: params.get('reference') || '',
  }

  if (filters.dateFrom && filters.dateTo && filters.dateFrom <= filters.dateTo) {
    return filters
  }

  const defaultFilters = getDefaultLedgerFilters(date)
  return { ...filters, dateFrom: defaultFilters.dateFrom, dateTo: defaultFilters.dateTo }
}

export const buildSearchFromFilters = (filters: InventoryLedgerFilters): string => {
  const params = new URLSearchParams()

  const normalizedDateFrom = normalizeDateFrom(filters.dateFrom)
  if (normalizedDateFrom) {
    params.set('date_from', normalizedDateFrom)
  }
  if (filters.dateTo) {
    params.set('date_to', filters.dateTo)
  }
  filters.categories.forEach((category) => params.append('category', category))
  if (filters.sku.trim()) {
    params.set('sku', filters.sku.trim())
  }
  if (filters.reference.trim()) {
    params.set('reference', filters.reference.trim())
  }

  return params.toString()
}

export const buildLedgerRequestSearch = (filters: InventoryLedgerFilters, page: number): string => {
  const params = new URLSearchParams()
  params.set('page', String(page < 1 ? DEFAULT_PAGE : page))

  const filterSearch = buildSearchFromFilters(filters)
  if (filterSearch) {
    const filterParams = new URLSearchParams(filterSearch)
    filterParams.forEach((value, key) => params.append(key, value))
  }

  return params.toString()
}

export const buildLedgerUrl = (filters: InventoryLedgerFilters, page: number, refreshNonce = 0): string => {
  const params = new URLSearchParams(buildLedgerRequestSearch(filters, page))
  if (refreshNonce > 0) {
    params.set('_retry', String(refreshNonce))
  }
  return `${LEDGER_ENDPOINT}?${params.toString()}`
}

export const buildExportFiltersPayload = (filters: InventoryLedgerFilters) => {
  const payload: Record<string, string | string[]> = {}

  const normalizedDateFrom = normalizeDateFrom(filters.dateFrom)
  if (normalizedDateFrom) payload.date_from = normalizedDateFrom
  if (filters.dateTo) payload.date_to = filters.dateTo
  if (filters.categories.length) payload.category = filters.categories
  if (filters.sku.trim()) payload.sku = filters.sku.trim()
  if (filters.reference.trim()) payload.reference = filters.reference.trim()

  return payload
}

export const getSourceReferenceLink = (category: string, sourceReference: string): string | null => {
  const normalizedCategory = category.trim().toLowerCase()

  if (!['order', 'asn', 'project'].includes(normalizedCategory)) {
    return null
  }

  const id = sourceReference.match(/#(\d+)/)?.[1]
  if (!id) {
    return null
  }

  if (normalizedCategory === 'order') return `/order/${id}/`
  if (normalizedCategory === 'asn') return `/asn/${id}/`
  return `/project/${id}/`
}
